import React, { lazy, ReactNode, Suspense, useEffect, useMemo } from "react";
import "./dev.scss";
import "./DefaultLayout.styles.scss";
import GridGlobalCss from "design-system/src/Components/Grid/GridGlobalCss";
import { usePageContext } from "@src/components/general/WrapPageElement/PageContext";
import { IDefaultHeaderProps } from "./DefaultHeader/DefaultHeader";
// import { SuspenseHelper } from "@src/components/general/SuspenseHelper/SuspenseHelper";
import { DrawerGlobalCss } from "design-system/src/Core/Drawer/Drawer.styled";
// import SuspenseAfterSetup from "@src/lib/AfterSetup/SuspenseAfterSetup";
import DeliveryOptionsProvider from "@src/layout/DefaultLayout/DefaultHeader/DeliveryOptionsProvider/DeliveryOptionsProvider";
import AuthDrawerProvider from "@src/backbone/provider/AuthDrawerProvider/AuthDrawerProvider";
import useDefaultLayoutDatas from "@src/layout/DefaultLayout/useDefaultLayoutDatas";
import SuspenseAfterInteraction from "@src/lib/AfterInteraction/SuspenseAfterInteraction";
import { InnerLayoutProps } from "@src/LayoutProvider";
import FakeBannerSection from "@src/components/general/FakeBannerSection/FakeBannerSection";
import { useWebViewContext } from "@src/lib/WebView/WebViewContext";
import EndOfService from "@src/lib/end-of-service/EndOfService";
import DefaultHeader from "./DefaultHeader/DefaultHeader";
import WidgetGroupLpCloseButton from "@src/layout/DefaultLayout/WidgetGroupLpChrome/WidgetGroupLpCloseButton";
import { installSsrListingProductOpen } from "@src/components/page-content/ProductCard/installSsrListingProductOpen";
import ProductModalSuspenseFallback from "@src/components/page-content/ProductModal/ProductModalSuspenseFallback";

const AlertNotice = lazy(() => import("@src/lib/alert-notice/AlertNotice"));
const OrderTracking = lazy(
  () =>
    import(
      "@src/layout/DefaultLayout/DefaultHeader/OrderTracking/OrderTracking"
    )
);
const NewsletterModal = lazy(
  () => import("@src/components/feedback/NewsletterModal/NewsletterModal")
);

const ProductModal = lazy(
  () => import("@src/components/page-content/ProductModal/ProductModal")
);
const SMSModeProvider = lazy(
  () => import("@src/backbone/provider/SMSModeProvider")
);

const CartDrawer = lazy(
  () => import("@src/components/page-content/Cart/CartDrawer")
);
const DefaultFooter = lazy(() => import("./DefaultFooter/DefaultFooter"));

const GATSBY_ENV_LEGACY = !!process.env.GATSBY_ENV_LEGACY;

/** Nav principale / LP plein écran — synchro build (+ preview via query dans wrap-page). */
export interface WidgetPageNavigation {
  isWidgetPage: boolean;
  displayMainNavigation: boolean;
}

export interface IDefaultLayoutProps extends InnerLayoutProps {
  children: ReactNode;
  wrapper?: IDefaultHeaderProps["wrapper"];
  widgetPageNavigation?: WidgetPageNavigation;
}

const DefaultLayout: React.FC<IDefaultLayoutProps> = (props) => {
  const { children, wrapper, isModal, widgetPageNavigation } = props;
  const defaultLayoutDatas = useDefaultLayoutDatas();
  const { isWebView } = useWebViewContext();
  const displayNav = !GATSBY_ENV_LEGACY && !isWebView;
  const { pageContext: pageCtx } = usePageContext();

  const pageTemplateFallback =
    pageCtx?.pageTemplate ??
    (pageCtx as { pageIntl?: { pageTemplate?: string } } | undefined)
      ?.pageIntl?.pageTemplate;

  const isWidgetPage =
    widgetPageNavigation?.isWidgetPage ??
    pageTemplateFallback === "Widget";

  const displayMainNavigation =
    widgetPageNavigation !== undefined
      ? widgetPageNavigation.displayMainNavigation
      : pageCtx?.display_main_navigation !== false;

  /** LP Widget sans nav principale : masquer header + footer (contenu plein écran + croix). */
  const isWidgetLpFullscreen = useMemo(
    () => isWidgetPage && !displayMainNavigation,
    [displayMainNavigation, isWidgetPage]
  );

  const showWidgetFullscreenClose = isWidgetLpFullscreen;

  const showSiteHeaderFooter = displayNav && !isWidgetLpFullscreen;

  const isListing = wrapper === "listing";

  // Listing SSR : délégation clic + preload modal dès le chrome (avant CategoryLayout lazy).
  useEffect(() => {
    if (!isListing) return;
    const uninstall = installSsrListingProductOpen();
    void import("@src/components/page-content/ProductModal/ProductModal");
    return uninstall;
  }, [isListing]);

  return (
    <>
      <div>
        <FakeBannerSection />
        <GridGlobalCss />
        <DrawerGlobalCss />
        {showSiteHeaderFooter && (
          <Suspense fallback={null}>
            <DefaultHeader
              wrapper={wrapper}
              datas={defaultLayoutDatas}
              isModal={isModal}
            />
          </Suspense>
        )}

        <DeliveryOptionsProvider />

        <AuthDrawerProvider />
        <EndOfService />

        {/* Listing : monter ProductModal hors AfterInteraction pour le 1er clic SSR. */}
        {isListing ? (
          <Suspense fallback={<ProductModalSuspenseFallback />}>
            <ProductModal />
          </Suspense>
        ) : null}

        <SuspenseAfterInteraction>
          <Suspense fallback={<div></div>}>
            <CartDrawer />
          </Suspense>
          <Suspense fallback={<></>}>
            <AlertNotice />
          </Suspense>
          <Suspense fallback={<></>}>
            <OrderTracking />
          </Suspense>
          {isListing ? null : (
            <Suspense fallback={<ProductModalSuspenseFallback />}>
              <ProductModal />
            </Suspense>
          )}
          <Suspense fallback={<></>}>
            <SMSModeProvider />
          </Suspense>
          <Suspense fallback={<></>}>
            <NewsletterModal />
          </Suspense>
        </SuspenseAfterInteraction>

        <main className="layout-content">
          <div className="layout-main">{children}</div>
        </main>

        {showWidgetFullscreenClose && <WidgetGroupLpCloseButton />}
        {showSiteHeaderFooter && (
          <footer className="layout-footer">
            <SuspenseAfterInteraction>
              <Suspense fallback={<div></div>}>
                <DefaultFooter datas={defaultLayoutDatas} />
              </Suspense>
            </SuspenseAfterInteraction>
          </footer>
        )}
      </div>
    </>
  );
};
DefaultLayout.displayName = "DefaultLayout";
export default React.memo(DefaultLayout);
